import constants from '@playcastdotio/GlobalConfig';
import { useRewardsState } from '../../../coordinators/rewards/hooks';

interface SettingsPageProps {
  onBack: () => void;
  onNavigate: (screen: 'account' | 'history' | 'how-points' | 'points-activity' | 'about-tiers' | 'program-terms') => void;
  onSignOut: () => void;
}

function NavRow({
  title,
  subtitle,
  trailing,
  onClick,
}: {
  title: string;
  subtitle?: string;
  trailing?: string;
  onClick?: () => void;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={!onClick}
      style={{
        width: '100%',
        padding: 10,
        borderRadius: 8,
        border: '1px solid rgba(228,228,228,0.12)',
        background: 'rgba(228,228,228,0.067)',
        cursor: onClick ? 'pointer' : 'default',
        textAlign: 'left',
      }}
    >
      <div style={{ display: 'flex', flexFlow: 'row', alignItems: 'center', justifyContent: 'start', gap: 8, width: '100%' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', flex: '1 1 0%', minWidth: 0 }}>
          <p style={{ margin: 0, color: 'rgba(228,228,228,0.92)', fontSize: 12, lineHeight: '16px', fontWeight: 590 }}>{title}</p>
          {subtitle && <p style={{ margin: 0, color: 'rgba(228,228,228,0.37)', fontSize: 12, lineHeight: '16px', fontWeight: 400 }}>{subtitle}</p>}
        </div>
        {trailing && (
          <p style={{ margin: '0 6px 0 0', color: 'rgba(228,228,228,0.37)', fontSize: 12, lineHeight: '16px', fontWeight: 400 }}>{trailing}</p>
        )}
        {onClick && <p style={{ margin: 0, color: 'rgba(228,228,228,0.37)', fontSize: 12, lineHeight: '16px', fontWeight: 400 }}>&rarr;</p>}
      </div>
    </button>
  );
}

export const SettingsPage = ({ onBack, onNavigate, onSignOut }: SettingsPageProps) => {
  const { claims } = useRewardsState();
  const redemptionCount = claims.filter((c) => c.status === 'fulfilled').length;

  const sectionLabel = (text: string) => (
    <p style={{ margin: '0 0 8px', color: 'rgba(228,228,228,0.37)', fontSize: 12, lineHeight: '16px', fontWeight: 590, letterSpacing: 0.5 }}>
      {text}
    </p>
  );

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box' }}>
      <button type='button' onClick={onBack}
        style={{ border: 'none', background: 'transparent', padding: 0, marginBottom: 12, cursor: 'pointer', textAlign: 'left', alignSelf: 'flex-start' }}>
        <p style={{ margin: 0, color: 'rgba(228,228,228,0.553)', fontSize: 12, lineHeight: '16px', fontWeight: 590 }}>&larr; Rewards</p>
      </button>
      <p style={{ margin: '0 0 12px', color: 'rgba(228,228,228,0.92)', fontSize: 20, lineHeight: '20px', fontWeight: 590 }}>Settings</p>

      <div className='hr-scroll' style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        {sectionLabel('HOSTING')}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', marginBottom: 12 }}>
          <NavRow title='About tiers' subtitle='How tiers work' onClick={() => onNavigate('about-tiers')} />
        </div>

        {sectionLabel('REWARDS')}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', marginBottom: 12 }}>
          <NavRow
            title='Redemption history'
            subtitle='Gift cards you have redeemed'
            trailing={redemptionCount ? `${redemptionCount}` : undefined}
            onClick={() => onNavigate('history')}
          />
          <NavRow title='How tokens work' subtitle='Earn, save, redeem, expire' onClick={() => onNavigate('how-points')} />
        </div>

        {sectionLabel('ACCOUNT')}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', marginBottom: 12 }}>
          <NavRow title='Account' subtitle='Profile, tokens, and linked logins' onClick={() => onNavigate('account')} />
        </div>

        {sectionLabel('RESOURCES')}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', marginBottom: 0 }}>
          <NavRow title='Get help' subtitle='Playcast Discord server' onClick={() => window.open('https://discord.gg/playcast', '_blank')} />
          <NavRow title='EULA' subtitle='End User License Agreement' onClick={() => onNavigate('program-terms')} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', marginTop: 28, marginBottom: 10 }}>
          <NavRow title='Sign out' subtitle='Sign out of Playcast on this device' onClick={onSignOut} />
        </div>

        <p style={{ margin: '4px 0 0', color: 'rgba(228,228,228,0.26)', fontSize: 10, lineHeight: '16px', fontWeight: 400, textAlign: 'center' }}>
          Playcast &middot; {constants.VERSION}
        </p>
      </div>
    </div>
  );
};
